import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { sharedStyles } from '../styles/sharedStyles';
import { getWorkDate, formatDateForDisplay, formatDateTimeForDisplay } from '../utils/timezone';
import { formatMachineOptionLabel, getTicketLineAmount } from '../utils/ticketLineAmount';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PublicTicketForm = () => {
  const [step, setStep] = useState('pincode'); // 'pincode' or 'form'
  const [pincode, setPincode] = useState('');
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'images'
  const [viewMode, setViewMode] = useState('submit'); // 'submit' or 'history'
  const [ticketHistory, setTicketHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageDescription, setImageDescription] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    pincode: '',
    customerId: '',
    jobId: '',
    employeeId: '',
    workDate: getWorkDate(),
    description: '',
    notes: ''
  });

  // Ticket items (machines)
  const [ticketItems, setTicketItems] = useState([
    {
      machineId: '',
      operatorId: '', // Operator per machine
      rateType: 'hourly',
      hoursUsed: '',
      loadsUsed: '',
      gallonsUsed: '',
      description: '',
      notes: ''
    }
  ]);

  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [employees, setEmployees] = useState([]); // For operator selection

  const verifyPincode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`/employees/verify-pincode`, { pincode });
      setEmployee(response.data.data);
      setFormData(prev => ({ ...prev, pincode, employeeId: response.data.data.id }));
      setStep('form');
      // Load form data after successful pincode verification
      await loadFormData();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid pincode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch ticket history for the employee
  const fetchTicketHistory = async () => {
    if (!employee || !pincode) return;
    
    setLoadingHistory(true);
    setError('');
    try {
      const response = await axios.get(`/tickets/public/history`, {
        params: {
          pincode,
          employeeId: employee.id
        }
      });
      
      if (response.data.success) {
        setTicketHistory(response.data.data.tickets || []);
      } else {
        setError(response.data.message || 'Error loading ticket history');
      }
    } catch (err) {
      console.error('Error fetching ticket history:', err);
      setError(err.response?.data?.message || 'Error loading ticket history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history when switching to history view
  useEffect(() => {
    if (viewMode === 'history' && employee && pincode) {
      fetchTicketHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, employee, pincode]);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Rejected':
        return 'danger';
      case 'Approved':
        return 'info';
      case 'Invoiced':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const loadFormData = async () => {
    setLoadingData(true);
    try {
      // Fetch active customers
      const customersResponse = await axios.get(`/customers?status=Active&limit=1000`);
      if (customersResponse.data.success) {
        const customersData = customersResponse.data.data.customers || customersResponse.data.data;
        setCustomers(Array.isArray(customersData) ? customersData : []);
      }

      // Initially, don't fetch jobs - they will be fetched when customer is selected
      // This avoids loading all jobs unnecessarily

      // Fetch active machines
      const machinesResponse = await axios.get(`/machines?status=Active&limit=1000`);
      if (machinesResponse.data.success) {
        const machinesData = machinesResponse.data.data.machines || machinesResponse.data.data;
        setMachines(Array.isArray(machinesData) ? machinesData : []);
      }

      // Fetch employees for operator selection (public endpoint)
      const employeesResponse = await axios.get(`/employees/public/active`);
      if (employeesResponse.data.success) {
        const employeesData = employeesResponse.data.data.employees || employeesResponse.data.data;
        setEmployees(Array.isArray(employeesData) ? employeesData : []);
      }
    } catch (err) {
      console.error('Error loading form data:', err);
      setError('Error loading form data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTicketItemChange = (index, field, value) => {
    const updatedItems = [...ticketItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    // If rateType changes, clear fields that are no longer relevant.
    if (field === 'rateType') {
      if (value !== 'hourly') updatedItems[index].hoursUsed = '';
      if (value !== 'load') updatedItems[index].loadsUsed = '';
      if (value === 'daily') updatedItems[index].loadsUsed = '';
    }
    setTicketItems(updatedItems);
  };

  const addTicketItem = () => {
    setTicketItems([
      ...ticketItems,
      {
        machineId: '',
        operatorId: employee?.id || '', // Default to logged-in employee
        rateType: 'hourly',
        hoursUsed: '',
        loadsUsed: '',
        gallonsUsed: '',
        description: '',
        notes: ''
      }
    ]);
  };

  const removeTicketItem = (index) => {
    if (ticketItems.length > 1) {
      const updatedItems = ticketItems.filter((_, i) => i !== index);
      setTicketItems(updatedItems);
    }
  };

  // Image management functions
  const handleImageRemoved = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      setError('Only image files are allowed');
      return;
    }
    
    setSelectedImages(prev => [...prev, ...imageFiles]);
    setError('');
  };

  // Camera effect: start/stop stream when showCamera changes
  useEffect(() => {
    if (showCamera && videoRef.current) {
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      }).then(stream => {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }).catch(err => {
        setError('Could not access camera. Please check permissions.');
      });
    }
    // Cleanup: stop camera when modal closes
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [showCamera]);

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedImage(file);
        setShowCamera(false);
      }, 'image/jpeg', 0.8);
    }
  };

  const addCapturedImage = () => {
    if (capturedImage) {
      setSelectedImages(prev => [...prev, capturedImage]);
      setCapturedImage(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form
    if (!formData.customerId || !formData.jobId || !formData.workDate) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    // Validate ticket items
    const validItems = ticketItems.filter(item => 
      item.machineId && 
      item.operatorId && 
      (item.rateType === 'daily' || (item.rateType === 'load' && item.loadsUsed) || (item.rateType === 'hourly' && item.hoursUsed))
    );
    if (validItems.length === 0) {
      setError('Please add at least one machine with operator and the required rate details.');
      setLoading(false);
      return;
    }

    // Clean up ticket items for backend
    const cleanedItems = validItems.map(item => ({
      machineId: item.machineId,
      employeeId: item.operatorId, // Use operatorId from form as employeeId for backend
      rateType: item.rateType,
      hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : null,
      loadsUsed: item.rateType === 'load' ? item.loadsUsed : null,
      gallonsUsed: item.gallonsUsed || 0,
      description: item.description,
      notes: item.notes
    }));

    try {
      // First, create the ticket
      const response = await axios.post(`/tickets/public`, {
        ...formData,
        ticketItems: cleanedItems
      });

      if (response.data.success) {
        const ticketId = response.data.data.id;

        // If there are selected images, upload them
        if (selectedImages.length > 0) {
          try {
            const formData = new FormData();
            selectedImages.forEach(file => {
              formData.append('images', file);
            });
            if (imageDescription) {
              formData.append('description', imageDescription);
            }

            await axios.post(`/tickets/${ticketId}/images`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (imageError) {
            console.error('Error uploading images:', imageError);
            // Don't fail the ticket creation if image upload fails
            // Just show a warning
            setSuccess('Ticket created successfully! (Some images may not have uploaded)');
            return;
          }
        }

        setSuccess('Ticket created successfully!');
        // Reset form
        setFormData({
          pincode: formData.pincode, // Keep the pincode
          customerId: '',
          jobId: '',
          employeeId: formData.employeeId, // Keep the employee ID
          workDate: getWorkDate(),
          description: '',
          notes: ''
        });
        setTicketItems([{
          machineId: '',
          operatorId: employee?.id || '',
          rateType: 'hourly',
          hoursUsed: '',
          loadsUsed: '',
          gallonsUsed: '',
          description: '',
          notes: ''
        }]);
        setSelectedImages([]);
        setImageDescription('');
      } else {
        setError(response.data.message || 'Error submitting ticket');
      }
    } catch (err) {
      console.error('Error submitting ticket:', err);
      setError(err.response?.data?.message || 'Error submitting ticket');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerJobs = (customerId) => {
    return jobs.filter(job => job.customerId === parseInt(customerId));
  };

  useEffect(() => {
    if (formData.customerId) {
      // Fetch all jobs for the selected customer (without status filter to get all active jobs)
      axios.get(`/jobs?customerId=${formData.customerId}&limit=1000`)
        .then(jobsResponse => {
          if (jobsResponse.data.success) {
            const jobsData = jobsResponse.data.data.jobs || jobsResponse.data.data;
            // Filter out deleted jobs and only show "In Progress" or "Pending" status jobs
            const filteredJobs = Array.isArray(jobsData) 
              ? jobsData.filter(job => 
                  !job.isDeleted && 
                  (job.status === 'In Progress' || job.status === 'Pending')
                )
              : [];
            setJobs(filteredJobs);
          } else {
            setJobs([]);
          }
        })
        .catch((err) => {
          console.error('Error loading jobs:', err);
          setJobs([]);
        });
      // Clear job selection if customer changes
      setFormData(prev => ({ ...prev, jobId: '' }));
    } else {
      setJobs([]);
      setFormData(prev => ({ ...prev, jobId: '' }));
    }
  }, [formData.customerId]);

  // Pincode verification form
  if (step === 'pincode') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: sharedStyles.colors.background.default,
        padding: '2rem 1rem'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h1 style={{
            ...sharedStyles.pageTitle,
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>Employee Verification</h1>
          
          {error && (
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              borderRadius: '8px',
              backgroundColor: sharedStyles.colors.danger.light,
              color: sharedStyles.colors.danger.main,
              border: `1px solid ${sharedStyles.colors.danger.main}`,
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={verifyPincode}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={sharedStyles.formLabel} htmlFor="pincode">Enter Your Pincode</label>
              <input
                type="password"
                id="pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                required
                placeholder="Enter your employee pincode"
                style={sharedStyles.formControl}
              />
            </div>

            <div style={{ textAlign: 'center' }}>
              <button
                type="submit"
                style={{
                  ...sharedStyles.button.primary,
                  width: '100%'
                }}
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify Pincode'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Main ticket form
  if (loadingData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: sharedStyles.colors.background.default
      }}>
        <div style={{
          ...sharedStyles.cardSubtitle,
          color: sharedStyles.colors.text.secondary
        }}>
          Loading form data...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: sharedStyles.colors.background.default,
      padding: '2rem 1rem'
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        width: '100%',
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        <div style={{
          backgroundColor: sharedStyles.colors.background.secondary,
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            ...sharedStyles.cardTitle,
            margin: 0
          }}>Welcome, {employee?.name}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* View Mode Tabs */}
            <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: 'white', padding: '0.25rem', borderRadius: '6px' }}>
              <button
                onClick={() => setViewMode('submit')}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: viewMode === 'submit' ? sharedStyles.colors.primary.main : 'transparent',
                  color: viewMode === 'submit' ? 'white' : sharedStyles.colors.text.primary,
                  cursor: 'pointer',
                  fontWeight: viewMode === 'submit' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                Submit Ticket
              </button>
              <button
                onClick={() => setViewMode('history')}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: viewMode === 'history' ? sharedStyles.colors.primary.main : 'transparent',
                  color: viewMode === 'history' ? 'white' : sharedStyles.colors.text.primary,
                  cursor: 'pointer',
                  fontWeight: viewMode === 'history' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                My Tickets
              </button>
            </div>
            <button 
              style={{
                ...sharedStyles.button.secondary,
                padding: '0.5rem 1rem'
              }}
              onClick={() => {
                setStep('pincode');
                setPincode('');
                setEmployee(null);
                setViewMode('submit');
                setTicketHistory([]);
                setFormData({
                  pincode: '',
                  customerId: '',
                  jobId: '',
                  employeeId: '',
                  workDate: getWorkDate(),
                  description: '',
                  notes: ''
                });
                setTicketItems([{
                  machineId: '',
                  operatorId: '',
                  rateType: 'hourly',
                  hoursUsed: '',
                  loadsUsed: '',
                  description: '',
                  notes: ''
                }]);
                setSelectedImages([]);
                setImageDescription('');
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {success}
            <button
              onClick={() => setSuccess('')}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                color: '#155724',
                cursor: 'pointer',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            backgroundColor: sharedStyles.colors.danger.light,
            color: sharedStyles.colors.danger.main,
            border: `1px solid ${sharedStyles.colors.danger.main}`,
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {error}
            <button
              onClick={() => setError('')}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                color: sharedStyles.colors.danger.main,
                cursor: 'pointer',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* History View */}
        {viewMode === 'history' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ ...sharedStyles.cardTitle, margin: 0 }}>My Ticket History</h3>
              <button
                onClick={fetchTicketHistory}
                style={{
                  ...sharedStyles.button.secondary,
                  padding: '0.5rem 1rem'
                }}
                disabled={loadingHistory}
              >
                {loadingHistory ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {loadingHistory ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{ marginTop: '1rem', color: sharedStyles.colors.text.secondary }}>Loading your tickets...</p>
              </div>
            ) : ticketHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <i className="bi bi-receipt" style={{ fontSize: '3rem', color: sharedStyles.colors.text.secondary }}></i>
                <p style={{ marginTop: '1rem', color: sharedStyles.colors.text.secondary }}>No tickets found</p>
                <p style={{ color: sharedStyles.colors.text.secondary, fontSize: '0.875rem' }}>You haven't submitted any tickets yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover" style={{ marginBottom: 0 }}>
                  <thead className="table-light">
                    <tr>
                      <th>Ticket #</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Job</th>
                      <th>Work Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketHistory.map(ticket => (
                      <tr key={ticket.id}>
                        <td>
                          <div className="fw-medium">{ticket.ticketNumber}</div>
                          <small className="text-muted">Created: {formatDateTimeForDisplay(ticket.createdAt)}</small>
                        </td>
                        <td>{formatDateForDisplay(ticket.createdAt)}</td>
                        <td>{ticket.Customer?.companyName || 'N/A'}</td>
                        <td>{ticket.Job?.title || 'N/A'}</td>
                        <td>{ticket.workDate ? formatDateForDisplay(ticket.workDate) : 'N/A'}</td>
                        <td>{formatCurrency(ticket.totalAmount)}</td>
                        <td>
                          <span className={`badge bg-${getStatusBadgeClass(ticket.status)}`}>
                            {ticket.status || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <i className="bi bi-eye me-1"></i>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Submit Ticket View */}
        {viewMode === 'submit' && (
          <>
            <h1 style={{
              ...sharedStyles.pageTitle,
              textAlign: 'center',
              marginBottom: '1.5rem'
            }}>Create New Ticket</h1>

            {/* Navigation Tabs */}
        <ul className="nav nav-tabs mb-3" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
              type="button"
            >
              <i className="bi bi-file-text me-2"></i>
              Details
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'images' ? 'active' : ''}`}
              onClick={() => setActiveTab('images')}
              type="button"
            >
              <i className="bi bi-images me-2"></i>
              Images ({selectedImages.length})
            </button>
          </li>
        </ul>

        {/* Details Tab */}
        {activeTab === 'details' && (
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="row g-3 mb-3">
              <div className="col-md-6 col-sm-12">
                <label className="form-label">Customer *</label>
                <select
                  className="form-select"
                  name="customerId"
                  value={formData.customerId}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select Customer</option>
                  {[...customers].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', undefined, { sensitivity: 'base' })).map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.companyName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 col-sm-12">
                <label className="form-label">Job Name *</label>
                <select
                  className="form-select"
                  name="jobId"
                  value={formData.jobId}
                  onChange={handleFormChange}
                  required
                  disabled={!formData.customerId}
                >
                  <option value="">Select Job Name</option>
                  {formData.customerId && getCustomerJobs(formData.customerId).map(job => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6 col-sm-12">
                <label className="form-label">Work Date *</label>
                <input
                  type="date"
                  className="form-control"
                  name="workDate"
                  value={formData.workDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="col-md-6 col-sm-12">
                <label className="form-label">Creator</label>
                <input
                  type="text"
                  className="form-control"
                  value={(employee?.email || employee?.name || '').trim()}
                  disabled
                  style={{ backgroundColor: '#f8f9fa' }}
                  title="Operator submitting this ticket"
                />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6 col-sm-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows="2"
                  placeholder="Brief description of work performed..."
                  style={{ minHeight: '60px', maxHeight: '120px' }}
                />
              </div>
              <div className="col-md-6 col-sm-12">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  rows="2"
                  placeholder="Additional notes..."
                  style={{ minHeight: '60px', maxHeight: '120px' }}
                />
              </div>
            </div>

            {/* Total Gallons Display */}
            {(() => {
              const totalGallons = ticketItems.reduce((sum, item) => {
                const gallons = parseFloat(item.gallonsUsed) || 0;
                return sum + gallons;
              }, 0);
              return totalGallons > 0 ? (
                <div className="alert alert-info mb-3 d-flex justify-content-between align-items-center">
                  <strong>TOTAL GALLONS USED:</strong>
                  <span className="badge bg-primary fs-6">{totalGallons.toFixed(2)} gallons</span>
                </div>
              ) : null;
            })()}

            {(() => {
              const estimatedTotal = ticketItems.reduce(
                (sum, item) => sum + getTicketLineAmount(item, machines),
                0
              );
              return estimatedTotal > 0 ? (
                <div className="alert alert-secondary mb-3 d-flex justify-content-between align-items-center">
                  <strong>ESTIMATED TICKET TOTAL:</strong>
                  <span className="badge bg-dark fs-6">${estimatedTotal.toFixed(2)}</span>
                </div>
              ) : null;
            })()}

            {/* Ticket Items (Machines) */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Equipment & Hours</h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={addTicketItem}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Machine
              </button>
            </div>

            {ticketItems.map((item, index) => (
              <div key={index} className="card mb-2 ticket-item-card">
                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-md-3 col-sm-6">
                      <label className="form-label">Machine *</label>
                      <select
                        className="form-select"
                        value={item.machineId}
                        onChange={(e) => handleTicketItemChange(index, 'machineId', e.target.value)}
                        required
                      >
                        <option value="">Select Machine</option>
                        {machines
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(machine => (
                            <option key={machine.id} value={machine.id}>
                              {formatMachineOptionLabel(machine)}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-3 col-sm-6">
                      <label className="form-label">Operator *</label>
                      <select
                        className="form-select"
                        value={item.operatorId}
                        onChange={(e) => handleTicketItemChange(index, 'operatorId', e.target.value)}
                        required
                      >
                        <option value="">Select Operator</option>
                        {employees
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(emp => (
                            <option key={emp.id} value={emp.id}>
                              {emp.name} {emp.position ? `- ${emp.position}` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-2 col-sm-6">
                      <label className="form-label">Rate Type *</label>
                      <select
                        className="form-select"
                        value={item.rateType}
                        onChange={e => handleTicketItemChange(index, 'rateType', e.target.value)}
                        required
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="load">Per Load</option>
                      </select>
                    </div>
                    {item.rateType === 'hourly' && (
                      <div className="col-md-2 col-sm-6">
                        <label className="form-label">Hours *</label>
                        <input
                          type="number"
                          className="form-control"
                          value={item.hoursUsed}
                          onChange={e => handleTicketItemChange(index, 'hoursUsed', e.target.value)}
                          step="0.5"
                          min="0"
                          required
                        />
                      </div>
                    )}
                    {item.rateType === 'load' && (
                      <div className="col-md-2 col-sm-6">
                        <label className="form-label">Loads Hauled *</label>
                        <input
                          type="number"
                          className="form-control"
                          value={item.loadsUsed}
                          onChange={e => handleTicketItemChange(index, 'loadsUsed', e.target.value)}
                          step="1"
                          min="0"
                          required
                        />
                      </div>
                    )}
                    <div className="col-md-2 col-sm-6">
                      <label className="form-label">Gallons Used</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.gallonsUsed}
                        onChange={e => handleTicketItemChange(index, 'gallonsUsed', e.target.value)}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-md-1 col-sm-6 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm w-100"
                        onClick={() => removeTicketItem(index)}
                        disabled={ticketItems.length === 1}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                  {item.machineId ? (
                    <div className="small text-muted mt-1">
                      Line amount:{' '}
                      <strong className="text-body">
                        ${getTicketLineAmount(item, machines).toFixed(2)}
                      </strong>
                      {item.rateType === 'daily' && (
                        <span> (daily rate × 1 day)</span>
                      )}
                    </div>
                  ) : null}
                  <div className="row g-2 mt-2">
                    <div className="col-md-6 col-sm-12">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.description}
                        onChange={e => handleTicketItemChange(index, 'description', e.target.value)}
                        placeholder="Work description for this machine..."
                      />
                    </div>
                    <div className="col-md-6 col-sm-12">
                      <label className="form-label">Notes</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.notes}
                        onChange={e => handleTicketItemChange(index, 'notes', e.target.value)}
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="text-center mt-4">
              <button
                type="submit"
                className="btn btn-primary btn-lg px-5"
                disabled={loading}
                style={{
                  backgroundColor: sharedStyles.colors.primary.main,
                  borderColor: sharedStyles.colors.primary.main,
                  padding: '1rem 3rem',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Creating...
                  </>
                ) : (
                  'Create Ticket'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Ticket Images</h6>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="bi bi-upload me-2"></i>
                  Choose Files
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={() => setShowCamera(true)}
                >
                  <i className="bi bi-camera me-2"></i>
                  Take Photo
                </button>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <div className="mb-3">
              <label className="form-label">Image Description (Optional)</label>
              <textarea
                className="form-control"
                value={imageDescription}
                onChange={(e) => setImageDescription(e.target.value)}
                rows="2"
                placeholder="Describe the images..."
              />
              <small className="text-muted">
                Maximum 10 images, 10MB each. Supported formats: JPG, PNG, GIF, WebP
              </small>
            </div>

            {selectedImages.length === 0 ? (
              <div className="text-center py-4">
                <i className="bi bi-images text-muted" style={{ fontSize: '3rem' }}></i>
                <p className="text-muted mt-2">No images selected yet</p>
                <p className="text-muted small">Click "Choose Files" or "Take Photo" to add images for this ticket</p>
              </div>
            ) : (
              <div className="row">
                {selectedImages.map((file, index) => (
                  <div key={index} className="col-md-4 col-lg-3 mb-3">
                    <div className="card h-100">
                      <div className="position-relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index + 1}`}
                          className="card-img-top"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="position-absolute top-0 end-0 p-2">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleImageRemoved(index)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                      <div className="card-body p-2">
                        <small className="text-muted d-block">
                          {file.name}
                        </small>
                        <small className="text-muted">
                          {formatFileSize(file.size)}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="text-center mt-4">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => setActiveTab('details')}
              >
                Back to Details
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setActiveTab('details')}
              >
                Continue to Details
              </button>
            </div>
          </div>
        )}
          </>
        )}

      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Take Photo</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCamera(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: '100%', maxWidth: '500px', minHeight: '300px', background: '#000' }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCamera(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={captureImage}
                >
                  <i className="bi bi-camera me-2"></i>
                  Capture Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={{ zIndex: 1055 }}
            onClick={() => {
              setShowTicketModal(false);
              setSelectedTicket(null);
            }}
          ></div>
          <div 
            className="modal fade show" 
            style={{ display: 'block', zIndex: 1060 }}
            onClick={() => {
              setShowTicketModal(false);
              setSelectedTicket(null);
            }}
          >
            <div 
              className="modal-dialog modal-dialog-centered modal-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ticket Details - {selectedTicket.ticketNumber}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTicketModal(false);
                    setSelectedTicket(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Customer:</strong> {selectedTicket.Customer?.companyName || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Job:</strong> {selectedTicket.Job?.title || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Work Date:</strong> {selectedTicket.workDate ? formatDateForDisplay(selectedTicket.workDate) : 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Status:</strong>{' '}
                    <span className={`badge bg-${getStatusBadgeClass(selectedTicket.status)}`}>
                      {selectedTicket.status || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Created:</strong> {formatDateTimeForDisplay(selectedTicket.createdAt)}
                  </div>
                  <div className="col-md-6">
                    <strong>Total Amount:</strong> {formatCurrency(selectedTicket.totalAmount)}
                  </div>
                </div>
                {selectedTicket.description && (
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <p>{selectedTicket.description}</p>
                  </div>
                )}
                {selectedTicket.notes && (
                  <div className="mb-3">
                    <strong>Notes:</strong>
                    <p>{selectedTicket.notes}</p>
                  </div>
                )}
                {selectedTicket.TicketItems && selectedTicket.TicketItems.length > 0 && (
                  <div className="mb-3">
                    <strong>Machines:</strong>
                    <table className="table table-sm mt-2">
                      <thead>
                        <tr>
                          <th>Machine</th>
                          <th>Operator</th>
                          <th>Hours / Loads</th>
                          <th>Rate Type</th>
                          <th>Gallons</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTicket.TicketItems.map((item, index) => (
                          <tr key={index}>
                            <td>{item.Machine?.name || 'N/A'}</td>
                            <td>{item.Employee?.name || 'N/A'}</td>
                            <td>
                              {item.rateType === 'daily'
                                ? 'Daily Rate'
                                : item.rateType === 'load'
                                  ? `${parseFloat(item.loadsUsed || 0).toFixed(0)} loads`
                                  : `${parseFloat(item.hoursUsed || 0).toFixed(1)} hrs`}
                            </td>
                            <td>
                              {item.rateType === 'daily'
                                ? 'Daily'
                                : item.rateType === 'load'
                                  ? 'Per Load'
                                  : 'Hourly'}
                            </td>
                            <td>{parseFloat(item.gallonsUsed || 0).toFixed(2)} gal</td>
                            <td>{formatCurrency(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTicketModal(false);
                    setSelectedTicket(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Captured Image Review Modal */}
      {capturedImage && (
        <div className="modal fade show" style={{ display: 'block', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Review Captured Photo</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setCapturedImage(null)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={URL.createObjectURL(capturedImage)}
                  alt="Captured"
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                  className="img-fluid"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCapturedImage(null)}
                >
                  Retake
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addCapturedImage}
                >
                  Use This Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicTicketForm; 